import * as React from 'react';
import {
    Alert, Box, Card, CardContent, Chip, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Editor from '@monaco-editor/react';
import { AiRequestResult, getAiEffectiveParams, getAiMeta, getAiServiceModels, getAiProviderKeys, getDashboardApiError, parseAiError } from '../../Api';
import {
    AiBudgetState, AiDataClass, AiEffectiveParamsDto, AiHealthStatus, AiMetaDto, AiModelDto, AiParams, AiProviderKeyDto,
    AiRunStatus, AiStatus, AiThinkingMode, AiUsageStatus,
} from '../../models/dtos/AiDto';

// ─── Formatting ──────────────────────────────────────────────────────────────

export function formatAiDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/** USD with precision that fits the magnitude: $0.000123, $0.0123, $12.34. */
export function formatAiUsd(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    const abs = Math.abs(value);
    const digits = abs === 0 ? 2 : abs < 0.01 ? 6 : abs < 1 ? 4 : 2;
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function formatAiCount(value: number | null | undefined): string {
    return value == null ? '—' : value.toLocaleString('en-US');
}

export function formatAiMs(value: number | null | undefined): string {
    if (value == null) return '—';
    if (value < 1000) return `${value} ms`;
    if (value < 60_000) return `${(value / 1000).toFixed(2)} s`;
    const seconds = Math.round(value / 1000);
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
    return `${Math.floor(seconds / 3600)} h ${Math.floor((seconds % 3600) / 60)} min`;
}

/** `rate` is a 0..1 fraction. */
export function formatAiRate(rate: number | null | undefined): string {
    return rate == null ? '—' : `${(rate * 100).toFixed(1)}%`;
}

export function formatAiDuration(from?: string | null, to?: string | null): string {
    if (!from || !to) return '—';
    const ms = new Date(to).getTime() - new Date(from).getTime();
    return Number.isNaN(ms) ? '—' : formatAiMs(ms);
}

/** YYYY-MM-DD (UTC) `daysAgo` days before today. */
export function aiUtcDay(daysAgo = 0): string {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - daysAgo);
    return date.toISOString().slice(0, 10);
}

/** Every day from..to inclusive (YYYY-MM-DD, UTC). */
export function aiDayRange(from: string, to: string): string[] {
    const days: string[] = [];
    const cursor = new Date(from + 'T00:00:00Z');
    const end = new Date(to + 'T00:00:00Z');
    if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return days;
    while (cursor.getTime() <= end.getTime() && days.length < 400) {
        days.push(cursor.toISOString().slice(0, 10));
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return days;
}

// ─── JSON / templates ────────────────────────────────────────────────────────

export function toJsonText(value: unknown): string {
    if (value === null || value === undefined) return '';
    return JSON.stringify(value, null, 2);
}

/** Empty → null. Invalid → `{ error }`. `requireObject` rejects arrays / scalars. */
export function parseJsonText<T = unknown>(text: string, label: string, requireObject = true): { value: T | null; error: string | null } {
    const trimmed = text.trim();
    if (!trimmed) return { value: null, error: null };
    try {
        const value = JSON.parse(trimmed);
        if (requireObject && (value === null || typeof value !== 'object' || Array.isArray(value))) {
            return { value: null, error: `Invalid JSON in ${label}: expected an object` };
        }
        return { value: value as T, error: null };
    } catch (e) {
        return { value: null, error: `Invalid JSON in ${label}: ${e instanceof Error ? e.message : String(e)}` };
    }
}

const VARIABLE_PATTERN = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;

/** `{{name}}` placeholders in order of first appearance. */
export function extractTemplateVariables(...texts: Array<string | null | undefined>): string[] {
    const found: string[] = [];
    texts.forEach(text => {
        if (!text) return;
        VARIABLE_PATTERN.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = VARIABLE_PATTERN.exec(text)) !== null) {
            if (!found.includes(match[1])) found.push(match[1]);
        }
    });
    return found;
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** Error text of the last failed GET under `label` (AI `{message, code}` parsed when present). */
export function aiLoadErrorText(label: string, fallback: string): string {
    const info = getDashboardApiError(label);
    if (info == null) return fallback;
    if (info.status != null) {
        const parsed = info.bodySnippet ? parseAiError(info.bodySnippet) : null;
        const detail = parsed
            ? `${parsed.code ? parsed.code + ': ' : ''}${parsed.message}`
            : '';
        return `${fallback} (HTTP ${info.status}${detail ? ' — ' + detail : ''})`;
    }
    return info.message ? `${fallback} (${info.message})` : fallback;
}

export type AiActionError = Pick<AiRequestResult<unknown>, 'error' | 'code' | 'status' | 'details'>;

export function toAiActionError(result: AiRequestResult<unknown>, prefix?: string): AiActionError | null {
    if (result.error == null) return null;
    return {
        error: prefix ? `${prefix}: ${result.error}` : result.error,
        code: result.code,
        status: result.status,
        details: result.details,
    };
}

export function textError(message: string): AiActionError {
    return { error: message, code: null, status: null, details: null };
}

/** Inline error: `[CODE] message`, plus `details` (missing variables, schema errors, budget…) as JSON. */
export function AiErrorAlert({ error, onClose, sx }: { error: AiActionError | null; onClose?: () => void; sx?: object }) {
    if (error == null || error.error == null) return null;
    const missing = Array.isArray(error.details?.missingVariables) ? (error.details?.missingVariables as unknown[]) : null;
    return (
        <Alert severity="error" onClose={onClose} sx={sx}>
            <Stack gap={0.5}>
                <Box>
                    {error.code && <Chip size="small" color="error" variant="outlined" label={error.code} sx={{ mr: 1 }} />}
                    {error.status != null && <Typography component="span" variant="caption" sx={{ mr: 1 }}>HTTP {error.status}</Typography>}
                    <Typography component="span" variant="body2">{error.error}</Typography>
                </Box>
                {missing && missing.length > 0 && (
                    <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                        <Typography variant="caption">Missing variables:</Typography>
                        {missing.map(v => <Chip key={String(v)} size="small" color="error" label={String(v)} />)}
                    </Stack>
                )}
                {error.details && Object.keys(error.details).length > 0 && (
                    <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(error.details, null, 2)}
                    </Box>
                )}
            </Stack>
        </Alert>
    );
}

// ─── Chips ───────────────────────────────────────────────────────────────────

type ChipColor = 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary';

export function healthColor(status?: AiHealthStatus | string | null): ChipColor {
    switch (status) {
        case 'HEALTHY': return 'success';
        case 'WARNING': return 'warning';
        case 'SICK': return 'warning';
        case 'DEAD': return 'error';
        default: return 'default';
    }
}

export const AI_HEALTH_HEX: Record<string, string> = {
    HEALTHY: '#4caf50', WARNING: '#ffc107', SICK: '#ff9800', DEAD: '#f44336',
};

export function AiHealthChip({ status, size = 'small' }: { status?: AiHealthStatus | string | null; size?: 'small' | 'medium' }) {
    return <Chip size={size} color={healthColor(status)} variant={status === 'SICK' ? 'outlined' : 'filled'} label={status ?? '—'} />;
}

export function budgetStateColor(state?: AiBudgetState | string | null): ChipColor {
    switch (state) {
        case 'OK': return 'success';
        case 'WARNING': return 'warning';
        case 'EXCEEDED': return 'error';
        case 'BLOCKING': return 'error';
        default: return 'default';
    }
}

export function AiBudgetStateChip({ state }: { state?: AiBudgetState | string | null }) {
    return <Chip size="small" color={budgetStateColor(state)} variant={state === 'EXCEEDED' ? 'outlined' : 'filled'} label={state ?? '—'} />;
}

export function AiStatusChip({ status }: { status?: AiStatus | string | null }) {
    if (status === 'ACTIVE') return <Chip size="small" color="success" variant="outlined" label="ACTIVE" />;
    if (status === 'DISABLED') return <Chip size="small" variant="outlined" label="DISABLED" />;
    if (status === 'DEPRECATED') return <Chip size="small" color="warning" variant="outlined" label="DEPRECATED" />;
    if (status === 'RETIRED') return <Chip size="small" color="error" variant="outlined" label="RETIRED" />;
    return <Chip size="small" variant="outlined" label={status ?? '—'} />;
}

export function AiRunStatusChip({ status }: { status?: AiRunStatus | string | null }) {
    const color: ChipColor = status === 'SUCCEEDED' ? 'success' : status === 'FAILED' ? 'error' : status === 'RUNNING' ? 'info' : 'default';
    return <Chip size="small" color={color} label={status ?? '—'} />;
}

export function AiUsageStatusChip({ status }: { status?: AiUsageStatus | string | null }) {
    const color: ChipColor = status === 'SUCCESS' ? 'success'
        : status === 'BLOCKED' ? 'warning'
            : status === 'REFUSAL' ? 'secondary'
                : status ? 'error' : 'default';
    return <Chip size="small" color={color} variant="outlined" label={status ?? '—'} />;
}

export function AiDataClassChip({ dataClass }: { dataClass?: AiDataClass | string | null }) {
    const color: ChipColor = dataClass === 'PERSONAL' ? 'error' : dataClass === 'INTERNAL' ? 'warning' : 'default';
    return <Chip size="small" color={color} variant="outlined" label={dataClass ?? '—'} />;
}

export function AiConfiguredChip({ configured }: { configured: boolean }) {
    return configured
        ? <Chip size="small" color="success" label="configured" />
        : <Chip size="small" color="error" label="not configured" />;
}

// ─── Small building blocks ───────────────────────────────────────────────────

export function CopyButton({ text, title = 'Copy' }: { text: string; title?: string }) {
    const [copied, setCopied] = React.useState(false);
    return (
        <Tooltip title={copied ? 'Copied' : title}>
            <IconButton size="small" onClick={() => {
                navigator.clipboard?.writeText(text).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1500);
                }).catch(() => undefined);
            }}>
                <ContentCopyIcon fontSize="inherit" />
            </IconButton>
        </Tooltip>
    );
}

/** `betup.ai.credentials.anthropic.default` with a copy button. */
export function CredentialProperty({ property }: { property?: string | null }) {
    if (!property) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
        <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {property}
            </Typography>
            <CopyButton text={property} title="Copy property name" />
        </Stack>
    );
}

export function JsonBlock({ value, maxHeight = 360 }: { value: unknown; maxHeight?: number }) {
    if (value === null || value === undefined) return <Typography variant="body2" color="text.secondary">—</Typography>;
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    return (
        <Box component="pre" sx={{
            m: 0, p: 1, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 12,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight, overflow: 'auto',
        }}>
            {text}
        </Box>
    );
}

export function TextBlock({ text, maxHeight = 360 }: { text?: string | null; maxHeight?: number }) {
    if (text == null || text === '') return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
        <Box sx={{
            p: 1, bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, fontSize: 13,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight, overflow: 'auto',
        }}>
            {text}
        </Box>
    );
}

export function StatLine({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} component="div">{value}</Typography>
        </Stack>
    );
}

export function KpiCard({ title, value, lines }: { title: string; value: React.ReactNode; lines?: Array<[string, React.ReactNode]> }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
                <Typography variant="h4" sx={{ my: 1 }}>{value}</Typography>
                {lines && (
                    <Stack gap={0.5}>
                        {lines.map(([label, v]) => <StatLine key={label} label={label} value={v} />)}
                    </Stack>
                )}
            </CardContent>
        </Card>
    );
}

/** Monaco editor: `plaintext` for prompts, `json` for schemas / params / bound args / variables. */
export function AiCodeEditor({ value, onChange, language, height = 240, readOnly = false, path, label, helper }: {
    value: string;
    onChange?: (next: string) => void;
    language: 'plaintext' | 'json';
    height?: number | string;
    readOnly?: boolean;
    /** Unique model path so several editors on a page keep separate buffers. */
    path: string;
    label?: string;
    helper?: React.ReactNode;
}) {
    return (
        <Stack gap={0.5}>
            {label && <Typography variant="subtitle2">{label}</Typography>}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Editor
                    height={height}
                    path={path}
                    defaultLanguage={language}
                    language={language}
                    value={value}
                    onChange={v => onChange?.(v ?? '')}
                    options={{
                        readOnly,
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        fontSize: 13,
                        tabSize: 2,
                        lineNumbers: language === 'json' ? 'on' : 'off',
                        renderLineHighlight: 'none',
                    }}
                />
            </Box>
            {helper && <Typography variant="caption" color="text.secondary" component="div">{helper}</Typography>}
        </Stack>
    );
}

// ─── Catalog (meta, models, provider keys) ───────────────────────────────────

export interface AiCatalog {
    meta: AiMetaDto | null;
    models: AiModelDto[];
    providerKeys: AiProviderKeyDto[];
    error: string | null;
    reload: () => void;
}

export function useAiCatalog(): AiCatalog {
    const [meta, setMeta] = React.useState<AiMetaDto | null>(null);
    const [models, setModels] = React.useState<AiModelDto[]>([]);
    const [providerKeys, setProviderKeys] = React.useState<AiProviderKeyDto[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [version, setVersion] = React.useState(0);

    React.useEffect(() => {
        let cancelled = false;
        Promise.all([getAiMeta(), getAiServiceModels(), getAiProviderKeys()]).then(([m, ms, keys]) => {
            if (cancelled) return;
            setMeta(m);
            setModels(ms ?? []);
            setProviderKeys(keys ?? []);
            const failed = [m == null ? 'meta' : null, ms == null ? 'models' : null, keys == null ? 'provider keys' : null]
                .filter(Boolean);
            setError(failed.length > 0 ? `Could not load ${failed.join(', ')} — pickers may be incomplete.` : null);
        });
        return () => { cancelled = true; };
    }, [version]);

    const reload = React.useCallback(() => setVersion(v => v + 1), []);
    return { meta, models, providerKeys, error, reload };
}

/** A select over an enum; `options` falls back to the contract list when meta is not loaded. */
export function EnumSelect<T extends string>({ label, value, options, onChange, allowEmpty, emptyLabel = 'Any', fullWidth = true, size, disabled, sx }: {
    label: string;
    value: T | '' | null;
    options: readonly T[];
    onChange: (value: T | '') => void;
    allowEmpty?: boolean;
    emptyLabel?: string;
    fullWidth?: boolean;
    size?: 'small' | 'medium';
    disabled?: boolean;
    sx?: object;
}) {
    return (
        <TextField select label={label} value={value ?? ''} fullWidth={fullWidth} size={size} disabled={disabled} sx={sx}
            onChange={e => onChange(e.target.value as T | '')}>
            {allowEmpty && <MenuItem value=""><em>{emptyLabel}</em></MenuItem>}
            {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
    );
}

export function modelLabel(model: AiModelDto): string {
    return `${model.modelKey} · ${model.provider}${model.status !== 'ACTIVE' ? ` (${model.status})` : ''}`;
}

/** `2` → `$2`, `0.3` → `$0.30`, `1.32` → `$1.32`: a price per million tokens. */
function perMtok(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return '—';
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(value) ? 0 : 2, maximumFractionDigits: 4 });
}

/** "$2 in · $10 out per 1M tokens" — a model's input vs output price, for the pickers' subtitles. */
export function modelPricing(model: AiModelDto): string {
    return `${perMtok(model.inputPerMtok)} in · ${perMtok(model.outputPerMtok)} out per 1M tokens`;
}

/** A model as a two-line option: key and provider, prices in gray below. */
function ModelOption({ model }: { model: AiModelDto }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body1" noWrap>{modelLabel(model)}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>{modelPricing(model)}</Typography>
        </Box>
    );
}

const THINKING_LABELS: Record<AiThinkingMode, string> = {
    MODEL_DEFAULT: 'model default',
    DISABLED: 'off',
    ADAPTIVE: 'adaptive',
    ENABLED: 'on',
};

/**
 * Read-only: what the selected model and fallback get for the params being edited (thinking, effort and why something
 * is dropped), as betup-server-ai resolves it. `agentCode`: a saved agent, whose links decide whether calls carry tools.
 */
export function AiEffectiveParams({ modelId, fallbackModelId, paramsText, agentCode }: {
    modelId: number | null;
    fallbackModelId: number | null;
    paramsText: string;
    agentCode?: string | null;
}) {
    const [result, setResult] = React.useState<AiEffectiveParamsDto | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const modelIds = [modelId, fallbackModelId].filter((id): id is number => id != null);
    const idsKey = modelIds.join(',');
    const parsed = parseJsonText<AiParams>(paramsText, 'params');

    React.useEffect(() => {
        if (idsKey === '' || parsed.error) return undefined;
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            const response = await getAiEffectiveParams({
                modelIds: idsKey.split(',').map(Number), params: parsed.value, agentCode: agentCode ?? null,
            });
            if (cancelled) return;
            setResult(response.data);
            setError(response.error);
        }, 400);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
        // parsed.value is derived from paramsText.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, paramsText, agentCode]);

    if (idsKey === '' || parsed.error) return null;
    if (error) return <Alert severity="warning">Could not show what the models get: {error}</Alert>;
    if (!result) return null;
    return (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                What the models get{result.hasTools ? ' (this agent offers tools)' : ''}
            </Typography>
            <Stack gap={1}>
                {result.models.map(model => (
                    <Box key={model.modelId}>
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {model.modelKey}{model.modelId === fallbackModelId && model.modelId !== modelId ? ' (fallback)' : ''}
                            </Typography>
                            <Chip size="small" variant="outlined" label={`Thinking: ${THINKING_LABELS[model.thinking]}`}
                                color={model.thinking === 'ADAPTIVE' || model.thinking === 'ENABLED' ? 'primary' : 'default'} />
                            <Chip size="small" variant="outlined" label={`Effort: ${model.effort ?? 'not sent'}`} />
                        </Stack>
                        {model.notes.map(note => (
                            <Typography key={note} variant="caption" color="text.secondary" component="div">{note}</Typography>
                        ))}
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

/** Model, fallback model and provider key pickers shared by the task and agent editors. */
export function AiModelPickers({ catalog, modelId, fallbackModelId, providerKeyId, onChange, disabled }: {
    catalog: AiCatalog;
    modelId: number | null;
    fallbackModelId: number | null;
    providerKeyId: number | null;
    onChange: (patch: { modelId?: number | null; fallbackModelId?: number | null; providerKeyId?: number | null }) => void;
    disabled?: boolean;
}) {
    const selectedModel = catalog.models.find(m => m.id === modelId) ?? null;
    const keysForProvider = catalog.providerKeys.filter(k => selectedModel == null || k.provider === selectedModel.provider);
    const toNum = (v: string) => (v === '' ? null : Number(v));
    // The selected model shows its prices too (two lines in the field).
    const renderModel = (value: unknown) => {
        const model = catalog.models.find(m => m.id === Number(value));
        return model ? <ModelOption model={model} /> : <em>none</em>;
    };
    return (
        <>
            <TextField select fullWidth label="Model" value={modelId ?? ''} disabled={disabled}
                SelectProps={{ renderValue: renderModel }}
                onChange={e => onChange({ modelId: toNum(e.target.value) })}>
                <MenuItem value=""><em>none</em></MenuItem>
                {catalog.models.map(m => <MenuItem key={m.id} value={m.id}><ModelOption model={m} /></MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Fallback model" value={fallbackModelId ?? ''} disabled={disabled}
                SelectProps={{ renderValue: renderModel }}
                onChange={e => onChange({ fallbackModelId: toNum(e.target.value) })}>
                <MenuItem value=""><em>none</em></MenuItem>
                {catalog.models.filter(m => m.id !== modelId).map(m => <MenuItem key={m.id} value={m.id}><ModelOption model={m} /></MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Provider key" value={providerKeyId ?? ''} disabled={disabled}
                helperText="Empty = pick automatically by priority and data class"
                onChange={e => onChange({ providerKeyId: toNum(e.target.value) })}>
                <MenuItem value=""><em>automatic</em></MenuItem>
                {keysForProvider.map(k => (
                    <MenuItem key={k.id} value={k.id}>
                        {k.provider.toLowerCase()} / {k.name} · max {k.maxDataClass}{k.status !== 'ACTIVE' ? ' (DISABLED)' : ''}{!k.configured ? ' — not configured' : ''}
                    </MenuItem>
                ))}
            </TextField>
        </>
    );
}

/** `"12"` → 12, `""` → null; NaN → undefined (invalid). */
export function parseOptionalNumber(text: string): number | null | undefined {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : undefined;
}

export const PARAMS_HELP = 'Optional: { "thinking": "disabled" | "adaptive", "effort": "low" | "medium" | "high" | "xhigh" | "max" }; "cache": "OFF" | "TOOLS" | "SYSTEM" (prompt caching for 1 hour; tasks default OFF, agents SYSTEM — set on the agent Prompt tab); agents also "contextTools" and "conversation". Params are the same for every model; what each selected model actually gets is shown below. No temperature: models always use their default (it is removed on save).';
